import React, { useState } from 'react';
import { AlertTriangle, TrendingDown, TrendingUp } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import BentoWidgetCard, { WidgetQuestion } from '../BentoWidgetCard';
import BaseDrawer from '../../../drawers/BaseDrawer';
import GradientHintCard from '../GradientHintCard';
import GradientChip from '../GradientChip';
import { getAnomaliesData, Anomaly } from '../../../../utils/aiBriefingsData';
interface AlertAnomalieWidgetProps {
  onHide?: () => void;
  onAsk?: (question: string) => void;
}
const AlertAnomalieWidget: React.FC<AlertAnomalieWidgetProps> = ({
  onHide,
  onAsk
}) => {
  const anomalies = getAnomaliesData();
  const [selectedAnomaly, setSelectedAnomaly] = useState<Anomaly | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const questions: WidgetQuestion[] = [{
    id: '1',
    text: 'Mostra solo anomalie critiche'
  }, {
    id: '2',
    text: 'Quali sono le cause?'
  }, {
    id: '3',
    text: 'Suggerisci azioni correttive'
  }];
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'Alta':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'Media':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Bassa':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  const handleAnomalyClick = (anomaly: Anomaly) => {
    setSelectedAnomaly(anomaly);
    setDrawerOpen(true);
  };
  return <>
      <BentoWidgetCard id="alert-anomalie" title="Alert & Anomalie" size="M" lastUpdated="5 min fa" questions={questions} onHide={onHide} onAsk={onAsk} data-id="element-1252">
        <div className="space-y-2" data-id="element-1253">
          {anomalies.map(anomaly => <div key={anomaly.id} onClick={() => handleAnomalyClick(anomaly)} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 cursor-pointer transition-colors" data-id="element-1254">
              <div className="flex items-start justify-between mb-2" data-id="element-1255">
                <div className="flex-1" data-id="element-1256">
                  <div className="flex items-center gap-2 mb-1" data-id="element-1257">
                    <AlertTriangle className={`h-4 w-4 ${anomaly.severity === 'Alta' ? 'text-red-600' : anomaly.severity === 'Media' ? 'text-yellow-600' : 'text-blue-600'}`} data-id="element-1258" />
                    <p className="text-sm font-medium text-gray-900" data-id="element-1259">
                      {anomaly.title}
                    </p>
                  </div>
                  <p className="text-xs text-gray-600" data-id="element-1260">{anomaly.metric}</p>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getSeverityColor(anomaly.severity)}`} data-id="element-1261">
                  {anomaly.severity}
                </span>
              </div>

              <div className="flex items-center justify-between" data-id="element-1262">
                <div className="flex items-center gap-4 text-xs" data-id="element-1263">
                  <div data-id="element-1264">
                    <span className="text-gray-500" data-id="element-1265">Atteso:</span>
                    <span className="font-medium text-gray-900 ml-1" data-id="element-1266">
                      {anomaly.expected}
                    </span>
                  </div>
                  <div data-id="element-1267">
                    <span className="text-gray-500" data-id="element-1268">Attuale:</span>
                    <span className="font-medium text-red-600 ml-1" data-id="element-1269">
                      {anomaly.actual}
                    </span>
                  </div>
                  <div className="flex items-center gap-1" data-id="element-1270">
                    {anomaly.actual < anomaly.expected ? <TrendingDown className="h-3 w-3 text-red-600" data-id="element-1271" /> : <TrendingUp className="h-3 w-3 text-green-600" data-id="element-1272" />}
                    <span className={`font-medium ${anomaly.actual < anomaly.expected ? 'text-red-600' : 'text-green-600'}`} data-id="element-1273">
                      {Math.abs(Math.round((anomaly.actual - anomaly.expected) / anomaly.expected * 100))}
                      %
                    </span>
                  </div>
                </div>

                <div className="w-20 h-8" data-id="element-1274">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-1275">
                    <LineChart data={anomaly.sparkline.map((v, i) => ({
                  value: v
                }))} data-id="element-1276">
                      <Line type="monotone" dataKey="value" stroke={anomaly.severity === 'Alta' ? '#DC2626' : anomaly.severity === 'Media' ? '#D97706' : '#2563EB'} strokeWidth={1.5} dot={false} data-id="element-1277" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>)}
        </div>
      </BentoWidgetCard>

      {/* Anomaly Detail Drawer */}
      <BaseDrawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} title={selectedAnomaly?.title || ''} description="Dettagli anomalia" width="lg" data-id="element-1278">
        {selectedAnomaly && <div className="space-y-4" data-id="element-1279">
            {/* Severity Badge */}
            <div className="flex items-center gap-2" data-id="element-1280">
              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getSeverityColor(selectedAnomaly.severity)}`} data-id="element-1281">
                Severità: {selectedAnomaly.severity}
              </span>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-2 gap-4" data-id="element-1282">
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-1283">
                <p className="text-sm text-gray-600 mb-1" data-id="element-1284">Valore Atteso</p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-1285">
                  {selectedAnomaly.expected}
                </p>
              </div>
              <div className="bg-red-50 rounded-lg p-4" data-id="element-1286">
                <p className="text-sm text-red-600 mb-1" data-id="element-1287">Valore Attuale</p>
                <p className="text-2xl font-bold text-red-900" data-id="element-1288">
                  {selectedAnomaly.actual}
                </p>
              </div>
            </div>

            {/* AI Explanation */}
            <GradientHintCard data-id="element-1289">
              <div className="flex items-start gap-2" data-id="element-1290">
                <AlertTriangle className="h-4 w-4 text-purple-600 flex-shrink-0 mt-0.5" data-id="element-1291" />
                <div data-id="element-1292">
                  <p className="text-xs font-semibold text-gray-900 mb-2" data-id="element-1293">
                    Spiegazione AI
                  </p>
                  <p className="text-sm text-gray-700" data-id="element-1294">
                    {selectedAnomaly.explanation}
                  </p>
                </div>
              </div>
            </GradientHintCard>

            {/* Suggested Filters */}
            {selectedAnomaly.suggestedFilters && selectedAnomaly.suggestedFilters.length > 0 && <div data-id="element-1295">
                  <p className="text-sm font-semibold text-gray-900 mb-2" data-id="element-1296">
                    Filtri Suggeriti
                  </p>
                  <div className="flex flex-wrap gap-2" data-id="element-1297">
                    {selectedAnomaly.suggestedFilters.map((filter, index) => <GradientChip key={index} size="sm" data-id="element-1298">
                        {filter}
                      </GradientChip>)}
                  </div>
                </div>}

            {/* Sparkline Chart */}
            <div data-id="element-1299">
              <p className="text-sm font-semibold text-gray-900 mb-2" data-id="element-1300">
                Trend Ultimi 7 Giorni
              </p>
              <div className="h-32 bg-gray-50 rounded-lg p-3" data-id="element-1301">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1302">
                  <LineChart data={selectedAnomaly.sparkline.map((v, i) => ({
                day: i + 1,
                value: v
              }))} data-id="element-1303">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" data-id="element-1304" />
                    <Line type="monotone" dataKey="value" stroke={selectedAnomaly.severity === 'Alta' ? '#DC2626' : selectedAnomaly.severity === 'Media' ? '#D97706' : '#2563EB'} strokeWidth={2} dot={{
                  fill: selectedAnomaly.severity === 'Alta' ? '#DC2626' : selectedAnomaly.severity === 'Media' ? '#D97706' : '#2563EB',
                  r: 4
                }} data-id="element-1305" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>}
      </BaseDrawer>
    </>;
};
export default AlertAnomalieWidget;